import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '../../../ui/toast';
import { getDashboardWellness, getUserAssessments } from '../../../utils/api_service/dashboard';
import {
  getApplicationsDetails,
  getDashboardDetails,
  getInterviewsDetails,
} from '../../../utils/api_service/user_api';
import { PARTICIPANT_PAGE_SIZE, PARTICIPANT_WELLNESS_PAGE_SIZE } from '../constants';
import { useParticipantTabs } from '../ParticipantDashboard/hooks';
import type {
  IApplicationData,
  IInterviewItem,
  IParticipantCardData,
  IParticipantDashboardData,
  IWellnessInviteData,
} from '../interfaces';

/**
 * Owns all participant-dashboard data: dashboard counts, applications,
 * assessments, interviews and wellness invites.
 * Tab/search/page state lives in useParticipantTabs (?tab=&q=&page=);
 * the view stays presentational and consumes this hook.
 */
export const useParticipantDashboard = () => {
  const {
    activeTab,
    setActiveTab,
    searchInput,
    setSearchTerm,
    debouncedSearch,
    page,
    setPage,
  } = useParticipantTabs();

  const [userAssessments, setUserAssessments] = useState<IParticipantCardData[]>([]);
  const [dashboardData, setDashboardData] = useState<IParticipantDashboardData | null>(null);
  const [applicationData, setApplicationData] = useState<IApplicationData[] | null>(null);
  const [interviewData, setInterviewData] = useState<IInterviewItem[] | null>(null);
  const [wellnessData, setWellnessData] = useState<IWellnessInviteData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dashLoading, setDashLoading] = useState<boolean>(false);
  const [wellnessLoading, setWellnessLoading] = useState<boolean>(false);

  // Separate total counts per paginated tab
  const [appTotalCount, setAppTotalCount] = useState(0);
  const [interviewTotalCount, setInterviewTotalCount] = useState(0);
  const [wellnessTotalCount, setWellnessTotalCount] = useState(0);

  const firstName = localStorage.getItem('firstname') ?? 'there';

  // Track mounted state to avoid setState on unmounted component (packaged build)
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const getInterviewsItems = useCallback(async (query: string, pageNumber: number) => {
    setDashLoading(true);
    try {
      const res = await getInterviewsDetails(query, pageNumber, PARTICIPANT_PAGE_SIZE);
      if (!isMountedRef.current) return;
      if (res.ok) {
        const result = await res.json();
        if (!isMountedRef.current) return;
        setInterviewData(result.interviews ?? []);
        setInterviewTotalCount(result.totalCount ?? 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (isMountedRef.current) setDashLoading(false);
    }
  }, []);

  const getApplicationItems = useCallback(async (query: string, pageNumber: number) => {
    setDashLoading(true);
    try {
      const res = await getApplicationsDetails(query, pageNumber, PARTICIPANT_PAGE_SIZE);
      if (!isMountedRef.current) return;
      if (res.ok) {
        const result = await res.json();
        if (!isMountedRef.current) return;
        setApplicationData(result.applications);
        setAppTotalCount(result.total ?? 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (isMountedRef.current) setDashLoading(false);
    }
  }, []);

  const getWellnessItems = useCallback(async (query: string, pageNumber: number) => {
    setWellnessLoading(true);
    try {
      const res = await getDashboardWellness(query, pageNumber, PARTICIPANT_WELLNESS_PAGE_SIZE);
      if (!isMountedRef.current) return;
      if (res.ok) {
        const result = await res.json();
        if (!isMountedRef.current) return;
        setWellnessData(result.assessments ?? []);
        setWellnessTotalCount(result.total ?? 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (isMountedRef.current) setWellnessLoading(false);
    }
  }, []);

  const refreshWellness = useCallback(() => {
    void getWellnessItems(debouncedSearch, page);
  }, [getWellnessItems, debouncedSearch, page]);

  const getDashItems = useCallback(async () => {
    setDashLoading(true);
    try {
      const res = await getDashboardDetails();
      if (!isMountedRef.current) return;
      if (res.ok) {
        const result: IParticipantDashboardData = await res.json();
        if (!isMountedRef.current) return;
        setDashboardData(result);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (isMountedRef.current) setDashLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'assessments') {
      let cancelled = false;
      setLoading(true);
      // Search is already debounced via useParticipantTabs; fetch directly.
      getUserAssessments(debouncedSearch)
        .then((response) => {
          if (cancelled || !isMountedRef.current) return;
          if (!response.ok) throw new Error();
          return response.json();
        })
        .then((data) => {
          if (cancelled || !isMountedRef.current) return;
          if (data) setUserAssessments(data);
        })
        .catch(() => {
          if (cancelled || !isMountedRef.current) return;
          toast.error('Error Fetching User Assessment Data', { position: 'bottom-center' });
        })
        .finally(() => {
          if (cancelled || !isMountedRef.current) return;
          setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }
    if (activeTab === 'applications') {
      void getApplicationItems(debouncedSearch, page);
    } else if (activeTab === 'interviews') {
      void getInterviewsItems(debouncedSearch, page);
    } else if (activeTab === 'wellness') {
      void getWellnessItems(debouncedSearch, page);
    }
    return undefined;
  }, [debouncedSearch, activeTab, page, getApplicationItems, getInterviewsItems, getWellnessItems]);

  useEffect(() => {
    void getDashItems();
  }, [getDashItems]);

  return {
    activeTab,
    setActiveTab,
    userAssessments,
    dashboardData,
    applicationData,
    interviewData,
    wellnessData,
    loading,
    dashLoading,
    wellnessLoading,
    searchTerm: searchInput,
    setSearchTerm,
    page,
    setPage,
    appTotalCount,
    interviewTotalCount,
    wellnessTotalCount,
    firstName,
    activeApplications: dashboardData?.counts.activeApplications ?? 0,
    pendingAssessments: dashboardData?.counts.pendingAssessments ?? 0,
    upcomingInterviews: dashboardData?.counts.upcomingInterviews ?? 0,
    getWellnessItems: refreshWellness,
  };
};
